<template>
  <div :class="[brandClasses.container, { 'o:hidden': upsellIdsInCart.length === upsellsWithInventory.length }]">
    <h2 v-if="title" :class="brandClasses.title">
      {{ title }}
    </h2>
    <vf-carousel-elevate
      v-if="showCarousel && hasMultipleUpsells"
      align="start"
      :class-container="brandClasses.upsellsWrapper"
      :show-arrows="false"
    >
      <product-upsell
        v-for="(upsell, i) in upsellsWithInventory"
        :key="upsell.id"
        :class="[brandClasses.upsellSlide, { 'o:hidden': upsellIdsInCart.includes(upsell.id) }]"
        data-test-id="product-upsell"
        :has-multiple-upsells
        :layout="itemLayout"
        :suppress-mini-cart
        :upsell
        @open-quickshop="$emit('openQuickshop', $event)"
        @rec-click="handleRecClick($event, i)"
      />
    </vf-carousel-elevate>
    <div v-else :class="[brandClasses.upsellsWrapper, hasMultipleUpsells && brandClasses.multipleUpsellsWrapper]">
      <product-upsell
        v-for="(upsell, i) in upsellsWithInventory"
        :key="upsell.id"
        :class="{ 'o:hidden': upsellIdsInCart.includes(upsell.id) }"
        data-test-id="product-upsell"
        :has-multiple-upsells
        :layout="itemLayout"
        :suppress-mini-cart
        :upsell
        @open-quickshop="$emit('openQuickshop', $event)"
        @rec-click="handleRecClick($event, i)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Product } from '#root/api/clients/product/data-contracts'
import type { Responsive } from '#types/common'
import type { ProductUpsell } from '#types/product'

export type Props = {
  skipCheckInventory?: boolean
  suppressMiniCart?: boolean
  title?: string
  upsells: ProductUpsell[]
}

export type Emits = {
  openQuickshop: [product: Product]
  recClick: [id: string]
}

const { skipCheckInventory, upsells } = defineProps<Props>()

const emit = defineEmits<Emits>()

const config = useAppConfig()
const { brandClasses, isCarousel, maxUpsells, upsellLayout } = config.components.product.upsells
const { enableColorAsAttribute } = config.pages.pdp
const cart = useCartStore()
const { $gtm, $viewport } = useNuxtApp()
const { trackEvent } = useOptimizely()

const inventory = ref()
const isInventoryLoaded = ref(false)

const fetchInventory = async () => {
  isInventoryLoaded.value = false
  if (skipCheckInventory || !upsells?.length) {
    inventory.value = []
  }
  else {
    inventory.value = await Promise.all(
      upsells.map(({ id }) => useApi().products.$inventory(
        getProductIdFromSku(id, { includeColor: !enableColorAsAttribute }) || id
      ))
    )
  }
  isInventoryLoaded.value = true
}

const upsellsWithInventory = computed(() => {
  const upsellsWithInventory = skipCheckInventory
    ? upsells
    : upsells
        .map((upsell, i) => {
          const { quantity = 0 } = inventory.value?.[i]?.variants[upsell.sku] || {}
          return {
            ...upsell,
            productInventoryState: quantity > 0 ? 'InStock' : 'OutOfStock'
          }
        })
        .filter(({ productInventoryState }) => productInventoryState === 'InStock')

  return upsellsWithInventory.slice(0, maxUpsells)
})

const hasMultipleUpsells = computed(() => upsellsWithInventory.value.length > 1)

const showCarousel = computed(() => getValueForBreakpoint(
  $viewport.breakpoint,
  isCarousel as boolean | Responsive<boolean> | undefined
))

const itemLayout = computed(() => upsellLayout ?? (hasMultipleUpsells.value ? 'vertical' : 'horizontal'))

const upsellIdsInCart = computed(() => upsellsWithInventory.value
  .filter(({ id }) => cart.items.find(({ masterId }) => id === masterId))
  .map(({ id }) => id)
)

const visibleUpsells = computed(() => upsellsWithInventory.value
  .filter(({ id }) => !upsellIdsInCart.value.includes(id))
)

const handleRecClick = (event: string, position: number) => {
  emit('recClick', event)
  trackEvent('upsell_product_click', { position })
}

const trackedUpsellIds = new Set<string>()
const trackCrossSellImpressions = () => {
  visibleUpsells.value.forEach((upsell) => {
    const eventLabel = upsell.analyticsId || upsell.id
    if (trackedUpsellIds.has(eventLabel)) return

    trackedUpsellIds.add(eventLabel)
    $gtm.push('upsell.onCrossSellImpression', eventLabel)
  })
}

watchImmediate(() => upsells, () => fetchInventory())

onMounted(() => {
  watch(visibleUpsells, trackCrossSellImpressions, {
    flush: 'post',
    immediate: true
  })
})
</script>
